import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import '../styles/footer.css';

const Footer = () => {
  const { t } = useTranslation('common');
  const { language } = useLanguage();

  return (
    <footer className="site-footer">
      <div className="footer-container">
        {/* Top Section - Slogan */}
        <div className="footer-top">
          <div className="footer-brand">
            <div className="footer-slogan-wrapper">
              <div className="slogan-line slogan-line-left"></div>
              <h2 className="footer-slogan">
                <span className="slogan-text">{t('footer.slogan.line1')}</span>
                <span className="slogan-text">{t('footer.slogan.line2')}</span>
                <span className="slogan-text">{t('footer.slogan.line3')}</span>
              </h2>
              <div className="slogan-line slogan-line-right"></div>
            </div>
          </div>
        </div>

        {/* Main Footer Links */}
        <div className="footer-main">
          {/* Company Section */}
          <div className="footer-column">
            <h4 className="footer-heading">{t('footer.company')}</h4>
            <ul className="footer-links">
              <li><Link to={`/${language}/about`}>{t('footer.aboutUs')}</Link></li>
              <li><Link to={`/${language}/clientele`}>{t('nav.clientele')}</Link></li>
              <li><Link to={`/${language}/contact`}>{t('nav.contact')}</Link></li>
            </ul>
          </div>

          {/* Applications Column */}
          <div className="footer-column">
            <h4 className="footer-heading">{t('nav.solutions')}</h4>
            <ul className="footer-links">
              <li><Link to={`/${language}/platform`}>{t('nav.platform')}</Link></li>
              <li><Link to={`/${language}/applications`}>{t('nav.solutions')}</Link></li>
              <li><Link to={`/${language}/knowledge-hub`}>{t('nav.mega.aiSolutions.items.knowledgeHub.label')}</Link></li>
              <li><Link to={`/${language}/e1-databridge`}>{t('nav.mega.aiSolutions.items.aiDataBridge.label')}</Link></li>
            </ul>
          </div>

          {/* AI Solutions Column */}
          <div className="footer-column">
            <h4 className="footer-heading">{t('nav.products')}</h4>
            <ul className="footer-links">
              <li><Link to={`/${language}/ai-governance`}>{t('footer.aiGovernance')}</Link></li>
              <li><Link to={`/${language}/enterprise-ai`}>{t('nav.enterpriseAI')}</Link></li>
              <li><Link to={`/${language}/knowledge-hub`}>{t('nav.mega.aiSolutions.items.knowledgeHub.label')}</Link></li>
              <li><Link to={`/${language}/ai-agents`}>{t('nav.aiAgents')}</Link></li>
              <li><Link to={`/${language}/ai-investigation`}>{t('nav.mega.aiSolutions.items.aiInvestigation.label')}</Link></li>
              <li><Link to={`/${language}/e1-databridge`}>{t('nav.mega.aiSolutions.items.aiDataBridge.label')}</Link></li>
            </ul>
          </div>

          {/* Industries Section */}
          <div className="footer-column footer-industries">
            <h4 className="footer-heading">{t('nav.industries')}</h4>
            <ul className="footer-links">
              <li><Link to={`/${language}/industries`}>{t('footer.allIndustries')}</Link></li>
              <li><Link to={`/${language}/industries#healthcare`}>{t('footer.healthcare')}</Link></li>
              <li><Link to={`/${language}/industries#fintech`}>{t('footer.fintech')}</Link></li>
              <li><Link to={`/${language}/industries#manufacturing`}>{t('footer.manufacturing')}</Link></li>
              <li><Link to={`/${language}/industries#government`}>{t('footer.government')}</Link></li>
            </ul>
          </div>

          {/* Contact Section */}
          <div className="footer-column footer-contact">
            <h4 className="footer-heading">{t('footer.getInTouch')}</h4>
            <div className="contact-info">
              <div className="contact-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
                <a href="mailto:sales@ewands.com">sales@ewands.com</a>
              </div>
              <div className="contact-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <a href="tel:+60378873252">+60 3-7887 3252</a>
              </div>
              <div className="contact-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                <span>
                  {t('footer.address.line1')}<br />
                  {t('footer.address.line2')}<br />
                  {t('footer.address.line3')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <div className="footer-copyright">
              {t('footer.copyright')}
            </div>
            <div className="footer-legal">
              <Link to={`/${language}/privacy-policy`}>{t('footer.privacyPolicy')}</Link>
              <span className="separator">•</span>
              <Link to={`/${language}/terms-of-use`}>{t('footer.termsOfUse')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
